import { create } from 'zustand';
import { API } from '../config/api';

// Cache of the frame catalog, keyed by both id and file_path. Slide elements
// store only a frameId (new) or a frameSrc path (legacy); at render time
// FrameElement resolves the live corners/screen/file_path from here so admin
// edits propagate to every already-placed frame without a manual refresh.
const useFramesStore = create((set, get) => ({
  byId: {},
  byPath: {},
  loaded: false,
  loading: false,

  fetchIfNeeded: async () => {
    const { loaded, loading } = get();
    if (loaded || loading) return;
    set({ loading: true });
    try {
      const res = await fetch(`${API.FRAMES}?limit=500`);
      if (!res.ok) throw new Error('load failed');
      const data = await res.json();
      const byId = {};
      const byPath = {};
      for (const f of data.frames || []) {
        byId[f.id] = f;
        if (f.file_path) byPath[f.file_path] = f;
      }
      set({ byId, byPath, loaded: true, loading: false });
    } catch {
      set({ loading: false });
    }
  },

  // Called by admin flows after create/update so FrameElement re-renders with
  // the fresh corners/screen without a page reload.
  ingestFrame: (frame) => {
    if (!frame || !frame.id) return;
    const byId = { ...get().byId, [frame.id]: frame };
    const byPath = { ...get().byPath };
    if (frame.file_path) byPath[frame.file_path] = frame;
    set({ byId, byPath, loaded: true });
  },

  removeFrame: (id) => {
    const { byId, byPath } = get();
    if (!byId[id]) return;
    const removedPath = byId[id].file_path;
    const nextById = { ...byId };
    const nextByPath = { ...byPath };
    delete nextById[id];
    if (removedPath) delete nextByPath[removedPath];
    set({ byId: nextById, byPath: nextByPath });
  },

  // Resolves a frame by id first, then by file_path (for pre-frameId elements).
  resolveFrame: (frameId, frameSrc) => {
    const { byId, byPath } = get();
    if (frameId && byId[frameId]) return byId[frameId];
    if (frameSrc && byPath[frameSrc]) return byPath[frameSrc];
    return null;
  },
}));

export default useFramesStore;
